import {
  Close,
  Content,
  Description,
  Overlay,
  Portal,
  Root,
  Title,
  Trigger,
} from "@radix-ui/react-dialog";
import { IconCrossLarge } from "central-icons";
import { Children, type ComponentProps, isValidElement, useMemo } from "react";
import { cn } from "@/lib/utils";
import { Button } from "./button";

function Dialog({ ...props }: ComponentProps<typeof Root>) {
  return <Root data-slot="dialog" {...props} />;
}

function DialogTrigger({ ...props }: ComponentProps<typeof Trigger>) {
  return <Trigger data-slot="dialog-trigger" {...props} />;
}

function DialogPortal({ ...props }: ComponentProps<typeof Portal>) {
  return <Portal data-slot="dialog-portal" {...props} />;
}

function DialogClose({ className, ...props }: ComponentProps<typeof Close>) {
  return (
    <Close asChild data-slot="dialog-close" {...props}>
      <Button className={cn(className)} size="iconXs" variant="secondary">
        <IconCrossLarge />
        <span className="sr-only">Close</span>
      </Button>
    </Close>
  );
}

function DialogOverlay({
  className,
  ...props
}: ComponentProps<typeof Overlay>) {
  return (
    <Overlay
      className={cn(
        "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-gray-800/20 data-[state=closed]:animate-out data-[state=open]:animate-in data-[state=closed]:backdrop-blur-none data-[state=open]:backdrop-blur-2xl",
        className
      )}
      data-slot="dialog-overlay"
      {...props}
    />
  );
}

function DialogContent({
  className,
  children,
  ...props
}: ComponentProps<typeof Content>) {
  const hasHeader = useMemo(
    () =>
      Children.toArray(children).some(
        (child) =>
          isValidElement(child) && child.type?.name === DialogHeader.name
      ),
    [children]
  );

  return (
    <DialogPortal data-slot="dialog-portal">
      <DialogOverlay />
      <Content
        className={cn(
          "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 fixed top-[50%] left-[50%] z-50 grid w-full max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] gap-4 rounded-smooth border bg-sidebar p-4 shadow-md duration-400 data-[state=closed]:animate-out data-[state=open]:animate-in sm:max-w-110",
          className
        )}
        data-slot="dialog-content"
        {...props}
      >
        {children}

        {!hasHeader && <DialogClose className="absolute top-4 right-4" />}
      </Content>
    </DialogPortal>
  );
}

function DialogHeader({
  className,
  children,
  ...props
}: ComponentProps<"div">) {
  return (
    <div
      className={cn(
        "grid grid-cols-[minmax(0,1fr)_auto] items-start gap-2 pl-2 text-center sm:text-left",
        className
      )}
      data-slot="dialog-header"
      {...props}
    >
      {children}
      <DialogClose className="justify-self-end" />
    </div>
  );
}

function DialogFooter({ className, ...props }: ComponentProps<"div">) {
  return (
    <div
      className={cn("flex gap-2 sm:flex-row sm:justify-center", className)}
      data-slot="dialog-footer"
      {...props}
    />
  );
}

function DialogTitle({ className, ...props }: ComponentProps<typeof Title>) {
  return (
    <Title
      className={cn("font-semibold text-2xl leading-none", className)}
      data-slot="dialog-title"
      {...props}
    />
  );
}

function DialogDescription({
  className,
  ...props
}: ComponentProps<typeof Description>) {
  return (
    <Description
      className={cn("p-2 text-gray-700 text-sm", className)}
      data-slot="dialog-description"
      {...props}
    />
  );
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
